import type { AgentBalanceHealthSnapshot, AgentBalanceItem } from '../../../types';

export const HEALTH_HEARTBEAT_WINDOW_MS = 24 * 60 * 60 * 1000;
/** Collapse burst snapshots that land in the same minute into one chart point. */
export const HEALTH_HEARTBEAT_BUCKET_MS = 60 * 1000;

export interface HealthHeartbeatCounts {
  healthy: number;
  outOfSync: number;
  downRisk: number;
  na: number;
}

export interface HealthHeartbeatPoint extends HealthHeartbeatCounts {
  ts: number;
  time: string;
  capturedAt: string;
}

function asCount(...values: unknown[]): number {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function toPoint(capturedAt: string, counts: HealthHeartbeatCounts): HealthHeartbeatPoint | null {
  const ts = new Date(capturedAt).getTime();
  if (!Number.isFinite(ts)) return null;
  return {
    ts,
    time: new Date(ts).toISOString(),
    capturedAt,
    healthy: asCount(counts.healthy),
    outOfSync: asCount(counts.outOfSync),
    downRisk: asCount(counts.downRisk),
    na: asCount(counts.na),
  };
}

export function countLiveHealth(items: AgentBalanceItem[] = []): HealthHeartbeatCounts {
  const counts: HealthHeartbeatCounts = { healthy: 0, outOfSync: 0, downRisk: 0, na: 0 };
  for (const item of items) {
    switch (item.healthStatus) {
      case 'healthy':
        counts.healthy += 1;
        break;
      case 'out_of_sync':
        counts.outOfSync += 1;
        break;
      case 'down_risk':
        counts.downRisk += 1;
        break;
      default:
        counts.na += 1;
    }
  }
  return counts;
}

export function snapshotToCounts(row: AgentBalanceHealthSnapshot | Record<string, unknown>): HealthHeartbeatCounts {
  return {
    healthy: asCount(row.healthy),
    outOfSync: asCount(
      (row as AgentBalanceHealthSnapshot).outOfSync,
      (row as { out_of_sync?: unknown }).out_of_sync,
    ),
    downRisk: asCount(
      (row as AgentBalanceHealthSnapshot).downRisk,
      (row as { down_risk?: unknown }).down_risk,
    ),
    na: asCount(row.na),
  };
}

export function healthHeartbeatDomain(
  points: HealthHeartbeatPoint[],
  now = new Date(),
): [number, number] {
  const end = Math.max(now.getTime(), ...points.map((point) => point.ts));
  return [end - HEALTH_HEARTBEAT_WINDOW_MS, end];
}

export function healthHeartbeatTicks(domain: [number, number], count = 7): number[] {
  const [start, end] = domain;
  if (count <= 1) return [end];
  const step = (end - start) / (count - 1);
  return Array.from({ length: count }, (_, i) => start + i * step);
}

function collapseSameMinutePoints(points: HealthHeartbeatPoint[]): HealthHeartbeatPoint[] {
  const byBucket = new Map<number, HealthHeartbeatPoint>();
  for (const point of points) {
    const bucket = Math.floor(point.ts / HEALTH_HEARTBEAT_BUCKET_MS);
    const existing = byBucket.get(bucket);
    if (!existing || point.ts >= existing.ts) {
      byBucket.set(bucket, point);
    }
  }
  return Array.from(byBucket.values()).sort((a, b) => a.ts - b.ts);
}

export function buildHealthHeartbeatPoints(
  snapshots: Array<AgentBalanceHealthSnapshot | Record<string, unknown>> = [],
  live?: HealthHeartbeatCounts | null,
  now = new Date(),
): HealthHeartbeatPoint[] {
  const points = collapseSameMinutePoints(
    snapshots
      .map((row) => toPoint(String(row.capturedAt ?? (row as { captured_at?: string }).captured_at ?? ''), snapshotToCounts(row)))
      .filter((point): point is HealthHeartbeatPoint => point != null),
  );

  if (live) {
    const last = points[points.length - 1];
    if (!last || now.getTime() - last.ts > HEALTH_HEARTBEAT_BUCKET_MS) {
      const livePoint = toPoint(now.toISOString(), live);
      if (livePoint) points.push(livePoint);
    }
  }

  return points;
}
